import { Component, Inject, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { DateTimezonePipe } from 'src/app/shared/pipes/date-timezone.pipe';
import { BasicService } from 'src/app/shared/services/basic.service';

import { Role, Team } from '../shared/models';
import { UsersService } from '../users.service';

@Component({
  selector: 'app-users-permissions',
  templateUrl: './users-permissions.component.html',
  styleUrls: ['./users-permissions.component.scss'],
})
export class UsersPermissionsComponent implements OnDestroy {
  routingTagExists: string;
  dropdownItems: any;
  selectedIndex = 0;
  company: any;
  users: any; //User[];
  roles: any[]; //Role[]
  allTeams: any[]; // Team[]
  teamMap: Map<number, any> = new Map();
  filteredUsers = [];
  filtersFormGroup = this.formBuilder.group({
    searchInput: [''],
    is_active: [[]],
    is_admin: [[]],
    teams: [[]],
    roles: [[]],
  });
  availableTeams = [];
  userCreateNameMaxLenth = 40;
  userCreateForm = this.formBuilder.group({
    name: ['', [Validators.required, Validators.maxLength(this.userCreateNameMaxLenth)]],
    email: ['', [Validators.email, Validators.required]],
  });
  roleCreateForm = this.formBuilder.group({
    name: ['', Validators.required],
  });
  teamCreateForm = this.formBuilder.group({
    name: ['', Validators.required],
    businessHours: this.formBuilder.group({
      timezone: ['', Validators.required],
      week: this.formBuilder.array([], Validators.required),
      exceptions: this.formBuilder.array([]),
    }),
  });
  replaceTeamControl = new FormControl();
  replaceTeamLabel = this.basicService.translate('users_roles/select_team');
  loading = false;

  @ViewChild('userCreateTemplateRef') userCreateTemplateRef: TemplateRef<any>;
  @ViewChild('roleCreateTemplateRef') roleCreateTemplateRef: TemplateRef<any>;
  @ViewChild('teamCreateTemplateRef') teamCreateTemplateRef: TemplateRef<any>;
  @ViewChild('teamReplaceTemplateRef') teamReplaceTemplateRef: TemplateRef<any>;

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private dateTimezonePipe: DateTimezonePipe,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private usersService: UsersService,
    @Inject('$stateParams') private $stateParams: any,
    @Inject('DropdownItemsFactory') private dropdownItemsService: any,
    @Inject('MediasFactory') private mediasService: any,
    @Inject('sites') private sites: any,
    @Inject('TopToolbarFactory') private topToolbarService: any
  ) {
    this.topToolbarService.setTitleTranslation('users_roles/title', null);
    this.company = this.alcmeonContextService.getCompany();
    this.routingTagExists = this.company.tags_groups.some(obj => obj.is_routing === 1); // Check if the routing_tag for this company exists

    // Open teams or roles tab when it is requested from an edition page
    if (this.$stateParams.tabIndex === 1 || this.$stateParams.tabIndex === 2) {
      this.selectedIndex = this.$stateParams.tabIndex;
    }
    this.selectedIndexChange(this.selectedIndex);

    this.users = [];
    this.refreshData();
  }

  private refreshData() {
    this.loading = true;
    this.basicService.showSpinner();
    forkJoin({
      users: this.usersService.getUsersV2(),
      teams: this.usersService.getTeams(this.routingTagExists),
      roles: this.usersService.getRoles(),
    })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.basicService.hideSpinner();
        })
      )
      .subscribe({
        next: ({ users, teams, roles }: { users: any[]; teams: Team[]; roles: Role[] }) => {
          const getLabelForNumber = (value: number) => this.basicService.translate(value ? 'yes' : 'no');
          this.roles = roles;
          this.usersService.sortUsersSitesRoles(this.roles, 'name');
          this.allTeams = teams;
          this.allTeams.forEach((team: Team) => {
            this.teamMap.set(team.tag_id, team);
          });
          const allSites = this.dropdownItemsService.buildSiteItems(this.sites);
          this.usersService.sortUsersSitesRoles(allSites, 'label');
          this.users = users;
          this.allTeams.forEach((team: Team) => {
            team.id = team.tag_id;
            team.label = team.name;
            team.created_by.email =
              team.created_by.admin_user_name || this.basicService.translate('admin/users/is_admin');
            team.last_modified_by.email =
              team.last_modified_by.admin_user_name || this.basicService.translate('admin/users/is_admin');
            team.createDateLabel = this.dateTimezonePipe.transform(team.created_by.when, 'shortDate');
            team.modifyDateLabel = this.dateTimezonePipe.transform(team.last_modified_by.when, 'shortDate');
            team.sso_config_ids_string = team.sso_config_ids?.join(',');
          });

          this.sortUserTeamsOrRoles(this.users, 'teams', this.allTeams); // Sort user teams according to the server order
          this.sortUserTeamsOrRoles(this.users, 'roles', this.roles); // Sort user roles alphabetically by name

          // Match ids with labels for user properties
          this.users.forEach(user => {
            user.isAdminLabel = getLabelForNumber(user.is_admin);
            user.isActiveLabel = getLabelForNumber(user.is_active);

            if (user.persona) {
              user.persona.image = user.persona.image_id
                ? this.mediasService.pictureUrl(this.company.id, user.persona.image_id)
                : null;
            }
            // Used for sorting columns with multiple entries
            user.sortTeams = user.teams.map(({ label }) => label).join(',');
            user.sortRoles = user.roles.map(({ label }) => label).join(',');
          });

          // Roles manipulation
          this.roles.forEach((role: Role) => {
            role.createDateLabel = this.dateTimezonePipe.transform(role.created_by.when, 'shortDate');
            role.modifyDateLabel = this.dateTimezonePipe.transform(role.last_modified_by.when, 'shortDate');
            role.created_by.email = role.created_by.admin_user_name;
            role.last_modified_by.email = role.last_modified_by.admin_user_name;
            role.sso_config_ids_string = role.sso_config_ids?.join(',');
          });

          this.filterUsers();
          this.filtersFormGroup.valueChanges.subscribe(() => this.filterUsers());

          this.dropdownItems = {
            yesOrNo: [
              { id: 0, label: this.basicService.translate('no') },
              { id: 1, label: this.basicService.translate('yes') },
            ],
            teams: this.allTeams,
            roles: this.roles.map(({ id, name }) => ({ id: id, label: name })),
          };
        },
      });
  }

  private addUser = () => {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'medium-size',
      data: {
        title: this.basicService.translate('users_roles/create_new_user'),
        templateRef: this.userCreateTemplateRef,
        disable: () => this.userCreateForm.invalid,
        confirmBtnText: this.basicService.translate('ok'),
        cancelBtnText: this.basicService.translate('cancel'),
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const user = this.userCreateForm.value;
        this.userCreateForm.reset();
        this.usersService.createUser(user).subscribe({
          next: (res: any) => {
            user.id = res.user_id;
            user.teams = [];
            user.roles = [];
            user.persona = {};
            user.is_active = 1;
            user.is_admin = 0;
            user.is_in_push_mode = 0;
            this.usersService.redirectToUserEdit(user);
          },
        });
      }
    });
  };

  private addRole = () => {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.basicService.translate('users_roles/create_new_role'),
        templateRef: this.roleCreateTemplateRef,
        disable: () => this.roleCreateForm.invalid,
        confirmBtnText: this.basicService.translate('ok'),
        cancelBtnText: this.basicService.translate('cancel'),
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const role = this.roleCreateForm.value;
        this.usersService.createRole(role).subscribe({
          next: (res: any) => {
            role.id = res.role_id;
            role.is_admin = 0;
            role.is_in_push_mode = 0;
            this.usersService.redirectToRoleEdit(role, true);
          },
        });
      }
    });
  };

  private addTeam = () => {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'medium-size',
      data: {
        title: this.basicService.translate('users_roles/create_new_team'),
        templateRef: this.teamCreateTemplateRef,
        disable: () => this.teamCreateForm.invalid,
        confirmBtnText: this.basicService.translate('ok'),
        cancelBtnText: this.basicService.translate('cancel'),
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const team = this.teamCreateForm.value;
        this.usersService.createTeam(team).subscribe({
          next: (res: any) => {
            team.id = res.tag_id;
            team.label = team.name;
            team.routable_teams = this.allTeams.map(team => team.id);
            this.teamCreateForm.reset();
            this.usersService.redirectToTeamEdit(team, true);
          },
        });
      }
    });
  };

  enableTeamsSave() {
    this.topToolbarService.updateButtonDisable(false, null);
  }

  private updateTeamsOrder = () => {
    this.usersService.updateTeamsOrder(this.alcmeonContextService.companyId, this.allTeams).subscribe({
      next: () => {
        this.topToolbarService.updateButtonDisable(true, null);
      },
    });
  };

  private filterUsers() {
    this.filteredUsers = this.users.filter(user => {
      const { value } = this.filtersFormGroup;
      const { searchInput, is_active, is_admin, teams, roles } = value;

      const hasFilter = searchInput || is_active?.length || is_admin?.length || teams?.length || roles?.length;
      return (
        !hasFilter ||
        ((!searchInput || `${user.name}#${user.email}`.toLowerCase().includes(searchInput.toLowerCase())) &&
          (!is_active?.length || is_active.some(value => user.is_active === value)) &&
          (!is_admin?.length || is_admin.some(value => user.is_admin === value)) &&
          (!teams?.length || teams.some(value => user.teams.some(({ id }) => id === value))) &&
          (!roles?.length || roles.some(value => user.roles.some(({ id }) => id === value))))
      );
    });
  }

  private sortUserTeamsOrRoles(users, toSort, sortBy) {
    users.forEach(user =>
      user[toSort].sort((x, y) => {
        if (sortBy.indexOf(sortBy.find(item => item.id === x)) < sortBy.indexOf(sortBy.find(item => item.id === y))) {
          return -1;
        }
        if (sortBy.indexOf(sortBy.find(item => item.id === x)) > sortBy.indexOf(sortBy.find(item => item.id === y))) {
          return 1;
        }
        return 0;
      })
    );
  }

  onUserClick(user) {
    this.usersService.redirectToUserEdit(user);
  }

  onRoleClick({ id }) {
    const item = this.usersService.roleMap.get(id);
    this.usersService.redirectToRoleEdit(item, 'roles');
  }

  onTeamClick({ id }) {
    const item = this.teamMap.get(id);
    this.usersService.redirectToTeamEdit(item, 'teams');
  }

  selectedIndexChange(selectedIndex) {
    this.selectedIndex = selectedIndex;
    // Set top right btn or btns depending on the selected tab
    if (this.selectedIndex === 0) {
      this.topToolbarService.setButton('users_roles/create_new_user', this.addUser, null);
      this.topToolbarService.clearSecondaryButton(null);
    } else if (this.selectedIndex === 1 && this.routingTagExists) {
      this.topToolbarService.setButton('save', this.updateTeamsOrder, null);
      this.topToolbarService.updateButtonDisable(true, null);
      this.topToolbarService.setSecondaryButton('users_roles/create_new_team', this.addTeam, null);
    } else {
      this.topToolbarService.setButton('users_roles/create_new_role', this.addRole, null);
      this.topToolbarService.clearSecondaryButton(null);
    }
  }

  deleteRole(id: number) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        description: this.basicService.translate('users_roles/confirm_role_deletion'),
        confirmBtnText: this.basicService.translate('ok'),
        cancelBtnText: this.basicService.translate('cancel'),
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.usersService.deleteRole(id).subscribe({
          next: () => this.refreshData(),
        });
      }
    });
  }

  replaceTeam(team) {
    this.availableTeams = this.allTeams.filter(t => t.id !== team.id).map(({ id, name }) => ({ id, label: name }));
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: `${this.basicService.translate('users_roles/delete_team')} "${team.name}" ${this.basicService.translate(
          'users_roles/replace_team'
        )}`,
        templateRef: this.teamReplaceTemplateRef,
        confirmBtnText: this.basicService.translate('ok'),
        cancelBtnText: this.basicService.translate('cancel'),
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.usersService.replaceTeam(team.tag_id, this.replaceTeamControl.value).subscribe({
          next: () => this.refreshData(),
        });
      }
    });
  }

  ngOnDestroy() {
    this.topToolbarService.clearButtons();
  }
}
